import React from 'react';
import { useTranslation } from 'react-i18next';
import { Product, ProductType, getProductImageUrl } from '../../../models/product/product';
import { Book, BookFormat } from '../../../models/product/book';

interface ProductCardProps {
  product: Product;
  productType: ProductType;
  onViewDetails: () => void;
  onAddToCart: (format?: BookFormat) => void;
  isRTL: boolean;
  isAddingToCart?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  productType,
  onViewDetails, 
  onAddToCart, 
  isRTL,
  isAddingToCart = false
}) => {
  const { t } = useTranslation();

  const handleAddToCart = () => {
    if (productType === ProductType.BOOK) {
      const book = product as Book;
      // Default to physical format, user can change in modal
      onAddToCart(book.bookFormat === BookFormat.BOTH ? BookFormat.PHYSICAL : book.bookFormat);
    } else {
      // For other product types, no format needed
      onAddToCart();
    }
  };

  // Product-specific rendering logic
  const renderProductSpecificInfo = () => {
    switch (productType) {
      case ProductType.BOOK:
        const book = product as Book;
        return (
          <>
            {/* Author */}
            <p className="text-sm text-gray-600 mb-2">
              {t('products.books.by-author', 'by')} <span className="font-medium">{book.author}</span>
            </p>

            {/* Category */}
            <p className="text-xs text-gray-500 mb-3">
              {t(`books.categories.${book.bookCategory}`, book.category)}
            </p>

            {/* Format Icons */}
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-1`}>
              {(book.bookFormat === BookFormat.PHYSICAL || book.bookFormat === BookFormat.BOTH) && (
                <div title={t('books.format.physical', 'Physical Book')}>
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              )}
              {(book.bookFormat === BookFormat.DIGITAL || book.bookFormat === BookFormat.BOTH) && (
                <div title={t('books.format.digital', 'Digital Book')}>
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
          </>
        );

      case ProductType.DEVOTIONALS:
        return (
          <p className="text-xs text-gray-500 mb-3">
            {t(`products.categories.devotionals`, 'Devotionals')}
          </p>
        );

      case ProductType.AUDIO:
        return (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {t(`products.categories.audio`, 'Audio')}
            </p>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            </div>
          </>
        );

      case ProductType.VIDEO:
        return (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {t(`products.categories.video`, 'Video')}
            </p>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
          </>
        );

      case ProductType.MUSIC:
        return (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {t(`products.categories.music`, 'Music')}
            </p>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
          </>
        );

      case ProductType.ART:
        return (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {t(`products.categories.art`, 'Art')}
            </p>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
              </svg>
            </div>
          </>
        );

      case ProductType.GIFTS:
        return (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {t(`products.categories.gifts`, 'Gifts')}
            </p>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 0v1m-2 0V6a2 2 0 00-2 0v1m2 0V6a2 2 0 012 0v1m-2 0H9m3 0h3m-3 0v13m0-13a2 2 0 00-2-2H7a2 2 0 00-2 2v3m4-3a2 2 0 012-2h3a2 2 0 012 2v3M5 11v8a2 2 0 002 2h10a2 2 0 002-2v-8M5 11V8" />
              </svg>
            </div>
          </>
        );

      default:
        return (
          <p className="text-xs text-gray-500 mb-3">
            {t(`products.categories.${productType}`, product.category)}
          </p>
        );
    }
  };

  // Product-specific badges
  const renderProductSpecificBadges = () => {
    if (productType === ProductType.BOOK) {
      // const book = product as Book; // Commented out - will be used when badges are re-enabled
      return (
        <>
          {/* Bestseller badge - commented out for future use */}
          {/* {book.bestseller && (
            <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              {t('general.badges.bestseller', 'Bestseller')}
            </span>
          )} */}
          {/* New Release badge - commented out for future use */}
          {/* {book.newRelease && (
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              {t('general.badges.new', 'New')}
            </span>
          )} */}
        </>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group flex flex-col h-full">
      {/* Product Cover */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <img
          src={getProductImageUrl(product)}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Badges */}
        <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} flex flex-col gap-2`}>
          {/* Discount badge - commented out for future use */}
          {/* {product.discount && product.discount > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              -{product.discount}%
            </span>
          )} */}
          {/* Featured badge - commented out for future use */}
          {/* {product.featured && (
            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              {t('general.badges.featured', 'Featured')}
            </span>
          )} */}
          {renderProductSpecificBadges()}
        </div>

        {/* Quick Actions Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <button
            onClick={onViewDetails}
            className="bg-white text-black px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors transform scale-90 group-hover:scale-100"
          >
            {t('general.actions.viewDetails', 'Quick View')}
          </button>
        </div>

        {/* Stock Status */}
        {!product.inStock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              {t('products.status.out-of-stock', 'Out of Stock')}
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 flex flex-col h-full">
        {/* Main Content Area - grows to fill available space */}
        <div className="flex-grow">
          {/* Title */}
          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-black transition-colors">
            {product.name}
          </h3>

          {/* Product-specific information */}
          {renderProductSpecificInfo()}

          {/* Price */}
          <div className={`flex items-center justify-between mb-3 ${isRTL ? 'space-x-reverse' : ''}`}>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-black">
                {product.price.toFixed(0)} EGP
              </span>
              {/* Original price - commented out with discount functionality */}
              {/* {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-sm text-gray-500 line-through">
                  {product.originalPrice.toFixed(0)} EGP
                </span>
              )} */}
            </div>
          </div>
        </div>

        {/* Action Buttons - positioned at bottom */}
        <div className={`flex gap-2 mt-4 ${isRTL ? 'space-x-reverse' : ''}`}>
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock || isAddingToCart}
            className="flex-1 bg-black text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isAddingToCart ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>
                  {t('cart.actions.adding-to-cart', 'Adding...')}
                </span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                </svg>
                <span>
                  {product.inStock 
                    ? t('cart.actions.add-to-cart', 'Add to Cart')
                    : t('products.status.out-of-stock', 'Out of Stock')
                  }
                </span>
              </>
            )}
          </button>
          
          <button
            onClick={onViewDetails}
            className="p-2 border border-gray-300 rounded-lg hover:border-black transition-colors"
            title={t('general.actions.view-details', 'View Details')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Backward compatibility - BookCard wrapper
interface BookCardProps {
  book: Book;
  onViewDetails: () => void;
  onAddToCart: (format?: BookFormat) => void;
  isRTL: boolean;
  isAddingToCart?: boolean;
}

export const BookCard: React.FC<BookCardProps> = (props) => {
  return (
    <ProductCard 
      product={props.book}
      productType={ProductType.BOOK}
      {...props}
    />
  );
};